import React, { useState, useEffect, useCallback } from "react";
import "./SupportCenter.scss";
import {
  getSupportTicketsApi,
  getSupportTicketByIdApi,
  createSupportTicketApi,
  resolveSupportTicketApi,
  addSupportTicketNoteApi,
  getSupportFaqsApi,
  createSupportFaqApi,
  updateSupportFaqApi,
  deleteSupportFaqApi,
  getSupportEscalationsApi,
  getDepartmentsApi,
} from "../../../api/api";

// ── constants ─────────────────────────────────────────────────────────────────
const TABS = ["Tickets", "FAQs", "Escalation Contacts"];

const STATUS_META = {
  OPEN: { label: "Open", cls: "sc-badge--open" },
  IN_PROGRESS: { label: "In Progress", cls: "sc-badge--inprogress" },
  RESOLVED: { label: "Resolved", cls: "sc-badge--resolved" },
  // lowercase fallbacks (UI filter values)
  open: { label: "Open", cls: "sc-badge--open" },
  inprogress: { label: "In Progress", cls: "sc-badge--inprogress" },
  resolved: { label: "Resolved", cls: "sc-badge--resolved" },
};

const PRIORITY_META = {
  HIGH: { label: "High", cls: "sc-priority--high" },
  MEDIUM: { label: "Medium", cls: "sc-priority--medium" },
  LOW: { label: "Low", cls: "sc-priority--low" },
  high: { label: "High", cls: "sc-priority--high" },
  medium: { label: "Medium", cls: "sc-priority--medium" },
  low: { label: "Low", cls: "sc-priority--low" },
};

// ── helpers ───────────────────────────────────────────────────────────────────
function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getInitials(name = "") {
  return (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span className={`sc-badge ${m.cls}`}>
      <span className="sc-badge__dot" />
      {m.label}
    </span>
  );
}

function PriorityTag({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.low;
  return <span className={`sc-priority ${m.cls}`}>{m.label}</span>;
}

function SectionTitle({ children }) {
  return <h3 className="sc-section-title">{children}</h3>;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonRow({ cols = 8 }) {
  return (
    <tr className="sc-skeleton-row">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}>
          <div
            className="sc-skeleton"
            style={{ width: i === 2 ? 180 : i === 0 ? 80 : 100, height: 13 }}
          />
        </td>
      ))}
    </tr>
  );
}

// ── TabBar ────────────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  return (
    <div className="sc-tabbar">
      {TABS.map((t) => (
        <button
          key={t}
          className={`sc-tabbar__tab ${active === t ? "sc-tabbar__tab--active" : ""}`}
          onClick={() => onChange(t)}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ── Filter chips ──────────────────────────────────────────────────────────────
function FilterChips({ active, onChange }) {
  const chips = [
    { key: "all", label: "All" },
    { key: "OPEN", label: "Open" },
    { key: "IN_PROGRESS", label: "In Progress" },
    { key: "RESOLVED", label: "Resolved" },
  ];
  return (
    <div className="sc-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          className={`sc-chip ${active === c.key ? "sc-chip--active" : ""}`}
          onClick={() => onChange(c.key)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

// ── KPI bar ───────────────────────────────────────────────────────────────────
function KpiBar({ tickets, total, loading }) {
  const open = tickets.filter((t) => t.status === "OPEN").length;
  const inprogress = tickets.filter((t) => t.status === "IN_PROGRESS").length;
  const resolved = tickets.filter((t) => t.status === "RESOLVED").length;

  const items = [
    { label: "Total Tickets", value: loading ? "—" : total, mod: "total" },
    { label: "Open", value: loading ? "—" : open, mod: "open" },
    {
      label: "In Progress",
      value: loading ? "—" : inprogress,
      mod: "inprogress",
    },
    { label: "Resolved", value: loading ? "—" : resolved, mod: "resolved" },
  ];

  return (
    <div className="sc-kpi-bar">
      {items.map((item) => (
        <div
          key={item.label}
          className={`sc-kpi-card sc-kpi-card--${item.mod}`}
        >
          <div className="sc-kpi-card__value">
            {loading ? (
              <div
                className="sc-skeleton"
                style={{ width: 32, height: 22, display: "inline-block" }}
              />
            ) : (
              item.value
            )}
          </div>
          <div className="sc-kpi-card__label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Ticket table ──────────────────────────────────────────────────────────────
function TicketTable({
  tickets,
  loading,
  error,
  onResolve,
  onAddNote,
  resolvingId,
}) {
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState(null);

  // per-ticket local notes (notes fetched from single-ticket API stored here)
  const [localNotes, setLocalNotes] = useState({});

  const handleOpenNote = async (ticketId) => {
    setNoteOpen(noteOpen === ticketId ? null : ticketId);
    setNoteText("");
    setNoteError(null);

    // fetch full ticket notes when expanding
    if (noteOpen !== ticketId) {
      try {
        const res = await getSupportTicketByIdApi(ticketId);
        const notes = res.data?.ticket?.notes || [];
        setLocalNotes((prev) => ({ ...prev, [ticketId]: notes }));
      } catch (_) {}
    }
  };

  const handleSaveNote = async (ticketId) => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    setNoteError(null);
    try {
      const res = await onAddNote(ticketId, { note: noteText.trim() });
      if (res?.note) {
        setLocalNotes((prev) => ({
          ...prev,
          [ticketId]: [...(prev[ticketId] || []), res.note],
        }));
      }
      setNoteText("");
      setNoteOpen(null);
    } catch (err) {
      setNoteError(err?.response?.data?.message || "Failed to save note.");
    } finally {
      setSavingNote(false);
    }
  };

  if (error) {
    return <div className="sc-error-banner">{error}</div>;
  }

  return (
    <div className="sc-table-wrap">
      <table className="sc-table">
        <thead>
          <tr>
            {[
              "Ticket ID",
              "User",
              "Issue",
              "Priority",
              "Status",
              "Assigned To",
              "Created",
              "Actions",
            ].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            : tickets.map((t) => (
                <React.Fragment key={t.id}>
                  <tr>
                    <td className="sc-table__id">
                      {t.id.length > 12 ? t.id.slice(0, 8) + "…" : t.id}
                    </td>
                    <td className="sc-table__name">{t.userName}</td>
                    <td className="sc-table__issue">{t.issue}</td>
                    <td>
                      <PriorityTag priority={t.priority} />
                    </td>
                    <td>
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="sc-table__assigned">
                      {t.assignedDepartment || "—"}
                    </td>
                    <td className="sc-table__muted">
                      {formatTime(t.createdAt)}
                    </td>
                    <td>
                      <div className="sc-table__actions">
                        <button
                          className="sc-btn sc-btn--secondary sc-btn--sm"
                          onClick={() => handleOpenNote(t.id)}
                        >
                          + Note
                        </button>
                        {t.status !== "RESOLVED" && (
                          <button
                            className="sc-btn sc-btn--primary sc-btn--sm"
                            onClick={() => onResolve(t.id)}
                            disabled={resolvingId === t.id}
                          >
                            {resolvingId === t.id ? "Resolving…" : "Resolve"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Inline notes row */}
                  {(localNotes[t.id]?.length > 0 || noteOpen === t.id) && (
                    <tr className="sc-table__notes-row">
                      <td colSpan={8}>
                        <div className="sc-notes">
                          {(localNotes[t.id] || []).map((n) => (
                            <div key={n.id} className="sc-notes__item">
                              <span className="sc-notes__avatar">
                                {getInitials(n.createdBy || "A")}
                              </span>
                              <span className="sc-notes__text">{n.note}</span>
                              <span className="sc-notes__time">
                                {formatTime(n.createdAt)}
                              </span>
                            </div>
                          ))}
                          {noteOpen === t.id && (
                            <div className="sc-notes__compose">
                              {noteError && (
                                <div
                                  className="sc-error-banner"
                                  style={{ marginBottom: 8 }}
                                >
                                  {noteError}
                                </div>
                              )}
                              <input
                                className="sc-notes__input"
                                placeholder="Add an internal note…"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                onKeyDown={(e) =>
                                  e.key === "Enter" && handleSaveNote(t.id)
                                }
                                autoFocus
                              />
                              <button
                                className="sc-btn sc-btn--primary sc-btn--sm"
                                onClick={() => handleSaveNote(t.id)}
                                disabled={savingNote}
                              >
                                {savingNote ? "Saving…" : "Save"}
                              </button>
                              <button
                                className="sc-btn sc-btn--secondary sc-btn--sm"
                                onClick={() => {
                                  setNoteOpen(null);
                                  setNoteError(null);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

          {!loading && tickets.length === 0 && (
            <tr>
              <td colSpan={8} className="sc-table__empty">
                No tickets found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onPage, loading }) {
  if (totalPages <= 1) return null;
  return (
    <div className="sc-pagination">
      <button
        className="sc-pagination__btn"
        disabled={page === 1 || loading}
        onClick={() => onPage(page - 1)}
      >
        ‹ Prev
      </button>
      <span className="sc-pagination__info">
        Page {page} of {totalPages}
      </span>
      <button
        className="sc-pagination__btn"
        disabled={page === totalPages || loading}
        onClick={() => onPage(page + 1)}
      >
        Next ›
      </button>
    </div>
  );
}

// ── Create ticket drawer ──────────────────────────────────────────────────────
function CreateTicketDrawer({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    userName: "",
    issue: "",
    priority: "MEDIUM",
    assignedDepartment: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);

  // fetch departments when drawer opens
  useEffect(() => {
    if (!open) return;
    setDeptLoading(true);
    getDepartmentsApi()
      .then((res) => {
        // API returns { departments: [...] } or array directly
        const list = res.data?.departments || res.data || [];
        setDepartments(Array.isArray(list) ? list : []);
      })
      .catch(() => setDepartments([]))
      .finally(() => setDeptLoading(false));
  }, [open]);

  const handleChange = (field, val) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  const handleCreate = async () => {
    if (!form.userName.trim() || !form.issue.trim()) {
      setError("User name and issue description are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        userName: form.userName.trim(),
        issue: form.issue.trim(),
        priority: form.priority,
        ...(form.assignedDepartment && {
          assignedDepartment: form.assignedDepartment,
        }),
      };
      const res = await createSupportTicketApi(payload);
      onCreated(res.data?.ticket);
      setForm({
        userName: "",
        issue: "",
        priority: "MEDIUM",
        assignedDepartment: "",
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create ticket.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="sc-drawer-overlay" onClick={onClose}>
      <div className="sc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="sc-drawer__header">
          <h3 className="sc-drawer__title">Create Ticket</h3>
          <button className="sc-drawer__close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="sc-drawer__body">
          {error && <div className="sc-error-banner">{error}</div>}

          <div className="sc-field">
            <label className="sc-field__label">User / Reporter *</label>
            <input
              className="sc-field__input"
              placeholder="e.g. Ahmed Al Balushi"
              value={form.userName}
              onChange={(e) => handleChange("userName", e.target.value)}
            />
          </div>
          <div className="sc-field">
            <label className="sc-field__label">Issue Description *</label>
            <textarea
              className="sc-field__textarea"
              placeholder="Describe the issue…"
              value={form.issue}
              onChange={(e) => handleChange("issue", e.target.value)}
              rows={3}
            />
          </div>
          <div className="sc-field">
            <label className="sc-field__label">Priority</label>
            <select
              className="sc-field__select"
              value={form.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
            >
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          <div className="sc-field">
            <label className="sc-field__label">Assigned Department</label>
            <select
              className="sc-field__select"
              value={form.assignedDepartment}
              onChange={(e) =>
                handleChange("assignedDepartment", e.target.value)
              }
              disabled={deptLoading}
            >
              <option value="">
                {deptLoading ? "Loading departments…" : "— Unassigned —"}
              </option>
              {departments.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="sc-drawer__footer">
          <button className="sc-btn sc-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="sc-btn sc-btn--primary"
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? "Creating…" : "Create Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FAQ accordion ─────────────────────────────────────────────────────────────
function FaqAccordion() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  // create / edit state
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    getSupportFaqsApi()
      .then((res) => setFaqs(res.data?.faqs || []))
      .catch((err) =>
        setError(err.response?.data?.message || "Failed to load FAQs."),
      )
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      setFormError("Question and answer are required.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await createSupportFaqApi({
        question: form.question.trim(),
        answer: form.answer.trim(),
      });
      setFaqs((prev) => [...prev, res.data.faq]);
      setForm({ question: "", answer: "" });
      setShowCreate(false);
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to create FAQ.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!form.answer.trim()) {
      setFormError("Answer is required.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await updateSupportFaqApi(id, { answer: form.answer.trim() });
      setFaqs((prev) => prev.map((f) => (f.id === id ? res.data.faq : f)));
      setEditingId(null);
      setForm({ question: "", answer: "" });
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to update FAQ.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteSupportFaqApi(id);
      setFaqs((prev) => prev.filter((f) => f.id !== id));
      if (open === id) setOpen(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete FAQ.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="sc-faq">
        {[1, 2, 3].map((i) => (
          <div key={i} className="sc-faq__item">
            <div
              className="sc-skeleton"
              style={{ width: "60%", height: 14, margin: "14px 0" }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {error && <div className="sc-error-banner">{error}</div>}

      {/* Create new FAQ */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          className="sc-btn sc-btn--primary sc-btn--sm"
          onClick={() => {
            setShowCreate(!showCreate);
            setFormError(null);
          }}
        >
          {showCreate ? "Cancel" : "+ Add FAQ"}
        </button>
      </div>

      {showCreate && (
        <div className="sc-faq-form">
          {formError && <div className="sc-error-banner">{formError}</div>}
          <div className="sc-field">
            <label className="sc-field__label">Question *</label>
            <input
              className="sc-field__input"
              value={form.question}
              onChange={(e) =>
                setForm((p) => ({ ...p, question: e.target.value }))
              }
              placeholder="Enter question…"
            />
          </div>
          <div className="sc-field">
            <label className="sc-field__label">Answer *</label>
            <textarea
              className="sc-field__textarea"
              rows={3}
              value={form.answer}
              onChange={(e) =>
                setForm((p) => ({ ...p, answer: e.target.value }))
              }
              placeholder="Enter answer…"
            />
          </div>
          <div className="sc-form-actions">
            <button
              className="sc-btn sc-btn--primary sc-btn--sm"
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save FAQ"}
            </button>
          </div>
        </div>
      )}

      <div className="sc-faq">
        {faqs.length === 0 && (
          <div className="sc-table__empty">No FAQs yet.</div>
        )}
        {faqs.map((f) => (
          <div
            key={f.id}
            className={`sc-faq__item ${open === f.id ? "sc-faq__item--open" : ""}`}
          >
            <button
              className="sc-faq__question"
              onClick={() => {
                setOpen(open === f.id ? null : f.id);
                setEditingId(null);
              }}
            >
              <span>{f.question}</span>
              <span className="sc-faq__chevron">
                {open === f.id ? "▲" : "▼"}
              </span>
            </button>

            {open === f.id && (
              <div className="sc-faq__answer">
                {editingId === f.id ? (
                  <div>
                    {formError && (
                      <div className="sc-error-banner">{formError}</div>
                    )}
                    <textarea
                      className="sc-field__textarea"
                      rows={3}
                      value={form.answer}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, answer: e.target.value }))
                      }
                    />
                    <div className="sc-form-actions" style={{ marginTop: 8 }}>
                      <button
                        className="sc-btn sc-btn--primary sc-btn--sm"
                        onClick={() => handleUpdate(f.id)}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Update"}
                      </button>
                      <button
                        className="sc-btn sc-btn--secondary sc-btn--sm"
                        onClick={() => {
                          setEditingId(null);
                          setFormError(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p style={{ margin: "0 0 10px" }}>{f.answer}</p>
                    <div className="sc-form-actions">
                      <button
                        className="sc-btn sc-btn--secondary sc-btn--sm"
                        onClick={() => {
                          setEditingId(f.id);
                          setForm({ question: f.question, answer: f.answer });
                          setFormError(null);
                        }}
                      >
                        ✎ Edit
                      </button>
                      <button
                        className="sc-btn sc-btn--secondary sc-btn--sm sc-btn--danger"
                        onClick={() => handleDelete(f.id)}
                        disabled={deletingId === f.id}
                      >
                        {deletingId === f.id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Escalation contacts ───────────────────────────────────────────────────────
// ── Escalation contacts (redesigned) ─────────────────────────────────────────
function EscalationContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);

  useEffect(() => {
    setLoading(true);
    getSupportEscalationsApi()
      .then((res) => setContacts(res.data?.contacts || []))
      .catch((err) =>
        setError(
          err.response?.data?.message || "Failed to load escalation contacts.",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopySuccess(email);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  if (loading) {
    return (
      <div className="esc-list">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="esc-card esc-card--skeleton">
            <div className="esc-card__avatar-wrap">
              <div
                className="sc-skeleton sc-skeleton--circle"
                style={{ width: 48, height: 48 }}
              />
            </div>
            <div className="esc-card__main">
              <div
                className="sc-skeleton"
                style={{ width: 140, height: 14, marginBottom: 8 }}
              />
              <div
                className="sc-skeleton"
                style={{ width: 190, height: 12, marginBottom: 6 }}
              />
              <div className="sc-skeleton" style={{ width: 110, height: 12 }} />
            </div>
            <div className="esc-card__side">
              <div
                className="sc-skeleton"
                style={{ width: 80, height: 24, borderRadius: 20 }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) return <div className="sc-error-banner">{error}</div>;

  return (
    <div className="esc-list">
      {contacts.length === 0 && (
        <div className="sc-table__empty">No escalation contacts found.</div>
      )}
      {contacts.map((c, idx) => {
        const initials = getInitials(c.fullName);
        const avatarColor = AVATAR_COLORS[idx % AVATAR_COLORS.length];

        return (
          <div key={c.id} className="esc-card">
            {/* Avatar */}
            <div className="esc-card__avatar-wrap">
              <div
                className="esc-card__avatar"
                style={{ background: avatarColor.bg, color: avatarColor.text }}
              >
                {initials}
              </div>
            </div>

            {/* Main info */}
            <div className="esc-card__main">
              <div className="esc-card__name-row">
                <span className="esc-card__name">{c.fullName}</span>
                {c.department?.name && (
                  <span className="esc-card__dept">{c.department.name}</span>
                )}
              </div>

              <div className="esc-card__contacts">
                {/* Email */}
                <div className="esc-card__contact-item">
                  <svg
                    className="esc-card__icon"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <rect
                      x="1"
                      y="3"
                      width="14"
                      height="10"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M1 5.5L8 10L15 5.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <a
                    href={`mailto:${c.email}`}
                    className="esc-card__email-link"
                    title={`Email ${c.fullName}`}
                  >
                    {c.email}
                  </a>
                  <button
                    className={`esc-card__copy-btn ${copySuccess === c.email ? "esc-card__copy-btn--copied" : ""}`}
                    onClick={() => handleCopyEmail(c.email)}
                    title="Copy email"
                  >
                    {copySuccess === c.email ? (
                      <>
                        <svg viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2 6L5 9L10 3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Copied
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 12 12" fill="none">
                          <rect
                            x="4"
                            y="4"
                            width="7"
                            height="7"
                            rx="1.2"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                          <path
                            d="M3 8H2a1 1 0 01-1-1V2a1 1 0 011-1h5a1 1 0 011 1v1"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                          />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>

                {/* Phone */}
                {c.mobile && c.mobile !== "—" && (
                  <div className="esc-card__contact-item">
                    <svg
                      className="esc-card__icon"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M3 2h3l1.5 3.5-2 1.5a9 9 0 003.5 3.5l1.5-2L14 10v3a1 1 0 01-1 1C5.163 14 2 7.837 2 3a1 1 0 011-1z"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <a
                      href={`tel:${c.mobile.replace(/\s/g, "")}`}
                      className="esc-card__phone-link"
                    >
                      {c.mobile}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Role badge */}
            <div className="esc-card__side">
              {c.role?.name && (
                <span className="esc-card__role-badge">
                  {c.role.name.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Avatar color palette — add near top of file with other constants
const AVATAR_COLORS = [
  { bg: "#EFF6FF", text: "#1D4ED8" },
  { bg: "#F0FDF4", text: "#15803D" },
  { bg: "#FFF7ED", text: "#C2410C" },
  { bg: "#FAF5FF", text: "#7E22CE" },
  { bg: "#FFF1F2", text: "#BE123C" },
  { bg: "#ECFDF5", text: "#065F46" },
  { bg: "#EFF6FF", text: "#0062FE" },
  { bg: "#FFFBEB", text: "#B45309" },
];

// ── Main page ─────────────────────────────────────────────────────────────────
function SupportCenter() {
  const [tab, setTab] = useState("Tickets");
  const [filter, setFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── tickets state ──────────────────────────────────────────────────────────
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [resolvingId, setResolvingId] = useState(null);

  const PAGE_LIMIT = 20;

  // ── fetch tickets ──────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const statusParam = filter === "all" ? "" : filter;
      const res = await getSupportTicketsApi(page, PAGE_LIMIT, statusParam);
      setTickets(res.data?.tickets || []);
      setTotalCount(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (err) {
      setFetchError(err.response?.data?.message || "Failed to load tickets.");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // ── resolve ticket ─────────────────────────────────────────────────────────
  const handleResolve = async (id) => {
    setResolvingId(id);
    try {
      const res = await resolveSupportTicketApi(id);
      const updated = res.data?.ticket;
      setTickets((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, ...updated, status: "RESOLVED" } : t,
        ),
      );
    } catch (err) {
      // non-blocking — ticket row stays as-is
      console.error("Resolve failed:", err.response?.data?.message);
    } finally {
      setResolvingId(null);
    }
  };

  // ── add note ───────────────────────────────────────────────────────────────
  const handleAddNote = async (ticketId, data) => {
    const res = await addSupportTicketNoteApi(ticketId, data);
    return res.data;
  };

  // ── ticket created callback ────────────────────────────────────────────────
  const handleTicketCreated = (newTicket) => {
    if (newTicket) {
      setTickets((prev) => [newTicket, ...prev]);
      setTotalCount((c) => c + 1);
    }
  };

  // ── filtered tickets (client-side on current page) ─────────────────────────
  const displayTickets =
    filter === "all" ? tickets : tickets.filter((t) => t.status === filter);

  return (
    <div className="sec-page sc-page">
      <div className="sc-page__inner">
        {/* Page header */}
        <div className="sc-page__header">
          <div className="sc-page__header-left">
            <h1 className="sc-page__title">Support Center</h1>
            <p className="sc-page__subtitle">
              Manage support tickets, browse FAQs and view escalation contacts.
            </p>
          </div>
          <div className="sc-page__header-actions">
            <button
              className="sc-btn sc-btn--secondary"
              onClick={fetchTickets}
              disabled={loading}
            >
              ↺ Refresh
            </button>
            <button
              className="sc-btn sc-btn--primary"
              onClick={() => setDrawerOpen(true)}
            >
              + Create Ticket
            </button>
          </div>
        </div>

        {/* KPI bar */}
        <KpiBar tickets={tickets} total={totalCount} loading={loading} />

        {/* Main tabbed card */}
        <div className="sc-card sc-card--tabs">
          <TabBar active={tab} onChange={setTab} />

          <div className="sc-tab-content">
            {tab === "Tickets" && (
              <>
                <div className="sc-tab-content__toolbar">
                  <SectionTitle>Support Tickets</SectionTitle>
                  <FilterChips
                    active={filter}
                    onChange={(f) => {
                      setFilter(f);
                      setPage(1);
                    }}
                  />
                </div>
                <TicketTable
                  tickets={displayTickets}
                  loading={loading}
                  error={fetchError}
                  onResolve={handleResolve}
                  onAddNote={handleAddNote}
                  resolvingId={resolvingId}
                />
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPage={setPage}
                  loading={loading}
                />
              </>
            )}

            {tab === "FAQs" && (
              <>
                <SectionTitle>Frequently Asked Questions</SectionTitle>
                <FaqAccordion />
              </>
            )}

            {tab === "Escalation Contacts" && (
              <>
                <SectionTitle>Escalation Contacts</SectionTitle>
                <EscalationContacts />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create ticket drawer */}
      <CreateTicketDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={handleTicketCreated}
      />
    </div>
  );
}

export default SupportCenter;
